import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, {
  useContext,
} from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import {
  Link, useLocation,
} from 'react-router-dom';
import classNames from 'classnames';
import {
  faEnvelope, faPlus,
  faRedo,
} from '@fortawesome/free-solid-svg-icons';
import useBulkSelect from '../../hooks/useBulkSelect';
import { EmailContext } from '../../context/Context';
import usePagination from '../../hooks/usePagination';
import IconButton from '../../common/IconButton';
import Flex from '../../common/Flex';
import InboxRow from './InboxRow';
import InboxFooter from './InboxFooter';
import EmailProvider from '../EmailProvider';
import { CiatAppContext } from '../../../../providers';
import useReadMessage from '../../hooks/useReadMessage';

function InboxTitle({ className, children, ...rest }) {
  return (
    <h5
      className={classNames(
        className,
        'fs-0 px-3 pt-3 pb-2 mb-0 border-bottom border-200',
      )}
      {...rest}
    >
      {children}
    </h5>
  );
}

function ActionButton({
  tooltip,
  icon,
  handleClick,
  variant = 'falcon-default',
}) {
  return (
    <OverlayTrigger
      overlay={<Tooltip style={{ position: 'fixed' }}>{tooltip}</Tooltip>}
    >
      <Button variant={variant} onClick={handleClick} className="shdaow-none">
        <FontAwesomeIcon icon={icon} />
      </Button>
    </OverlayTrigger>
  );
}

function InboxElement({ openSettings }) {
  const { emails, fetchEmails } = useContext(EmailContext);

  const {
    paginationState: {
      data: paginatedEmails,
      totalItems,
      canNextPage,
      canPreviousPage,
      from,
      to,
    },
    nextPage,
    prevPage,
  } = usePagination(emails, 10);

  const unreadEmails = paginatedEmails.filter((email) => !email.read);
  const otherEmails = paginatedEmails.filter((email) => email.read);
  const emailIds = emails && emails.map((email) => email.id);
  const unreadIds = unreadEmails && unreadEmails.map((email) => email.id);
  const otherIds = otherEmails && otherEmails.map((email) => email.id);

  const {
    selectedItems,
    selectedUnreadItems,
    selectedOtherItems,
    isSelectedItem,
    // isIndeterminate,
    toggleSelectedItem,
    toggleUnreadAllSelected,
    toggleAllOthersSelected,
    isAllUnreadSelected,
    isAllOtherSelected,
  } = useBulkSelect(emailIds, unreadIds, otherIds);

  const { changeRead } = useReadMessage();
  const { currentUser } = useContext(CiatAppContext);

  const handleActionButtonClick = () => {
    selectedItems.map((id) => changeRead(id));
    fetchEmails();
    toggleUnreadAllSelected(true);
    toggleAllOthersSelected(true);
  };

  return (
    <>
      {openSettings && (
        <InboxTitle className="fw-bold">Адміністрування</InboxTitle>
      )}
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col as={Flex} className="align-items-center">
              {selectedItems.length === 0 && (
                <>
                  <IconButton
                    variant="falcon-default"
                    size="sm"
                    className="ms-sm-1"
                    icon={faRedo}
                    onClick={() => { fetchEmails(); }}
                  />
                  <FontAwesomeIcon icon="redo" />
                </>
              )}
            </Col>
            {currentUser.is_superuser && (
              <Col xs="auto">
                <IconButton
                  variant="falcon-primary"
                  size="sm"
                  icon={faPlus}
                  as={Link}
                  to="/email/compose/create"
                  transform="shrink-3"
                >
                  Додати
                </IconButton>
              </Col>
            )}
          </Row>
        </Card.Header>
        <Card.Body className="fs--1 border-top border-200 p-0">
          {paginatedEmails.length > 0 ? (
            <>
              <Col as={Flex} className="align-items-center">
                {unreadEmails.length > 0 && (
                <>
                  <Form.Check
                    type="checkbox"
                    id="inboxBulkSelect"
                    className="mb-0 d-none d-sm-block form-check"
                    checked={isAllUnreadSelected}
                    onChange={() => toggleUnreadAllSelected(false)}
                  />
                  {selectedUnreadItems.length > 0 && (
                  <div className="ms-1">
                    <ButtonGroup size="sm">
                      <ActionButton
                        tooltip="Відмітити як прочитаний"
                        icon={faEnvelope}
                        handleClick={() => handleActionButtonClick()}
                      />
                    </ButtonGroup>
                  </div>
                  )}
                </>
                )}
                <InboxTitle>Непрочитані</InboxTitle>
              </Col>
              {unreadEmails.map((email) => (
                <InboxRow
                  email={email}
                  key={email.id}
                  isSelectedItem={isSelectedItem}
                  toggleSelectedItem={toggleSelectedItem}
                  openSettings={openSettings}
                />
              ))}
              <Col as={Flex} className="align-items-center">
                {otherEmails.length > 0 && (
                <>
                  <Form.Check
                    type="checkbox"
                    id="inboxBulkSelect"
                    className="mb-0 d-none d-sm-block form-check"
                    checked={isAllOtherSelected}
                    onChange={() => toggleAllOthersSelected(false)}
                  />
                  {selectedOtherItems.length > 0 && (
                  <div className="ms-1">
                    <ButtonGroup size="sm">
                      <ActionButton
                        tooltip="Відмітити як непрочитаний"
                        icon={faEnvelope}
                        handleClick={() => handleActionButtonClick()}
                      />
                    </ButtonGroup>
                  </div>
                  )}
                </>
                )}
                <InboxTitle>Прочитані</InboxTitle>
              </Col>
              {otherEmails.map((email) => (
                <InboxRow
                  email={email}
                  key={email.id}
                  isSelectedItem={isSelectedItem}
                  toggleSelectedItem={toggleSelectedItem}
                  openSettings={openSettings}
                />
              ))}
            </>
          ) : (
            <Alert variant="info" className="mb-0 rounded-0">
              <h5 className="alert-heading">Повідомлень немає</h5>
              <hr />
              <p className="mb-0">
                {'Повідомлення з\'являться тут автоматично.'}
              </p>
            </Alert>
          )}
        </Card.Body>
        <InboxFooter
          totalItems={totalItems}
          from={from}
          to={to}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          nextPage={nextPage}
          prevPage={prevPage}
        />
      </Card>
    </>
  );
}

function Inbox() {
  const location = useLocation();
  const openSettings = location.state && location.state.openSettings;

  return (
    <EmailProvider>
      <InboxElement openSettings={openSettings} />
    </EmailProvider>
  );
}

InboxTitle.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string.isRequired,
};

ActionButton.propTypes = {
  tooltip: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  variant: PropTypes.string.isRequired,
};

InboxElement.propTypes = {
  openSettings: PropTypes.bool.isRequired,
};

export default Inbox;
